import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalUnsubscribe() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("processing"); // processing | success | error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const processUnsubscribe = async () => {
      const email = searchParams.get("email");
      const token = searchParams.get("token");

      if (!email || !token) {
        setStatus("error");
        setErrorMessage("Invalid unsubscribe link. Missing required parameters.");
        return;
      }

      try {
        const response = await axios.get(`${API}/portal/unsubscribe`, {
          params: { email, token }
        });

        if (response.data.success) {
          setStatus("success");
        } else {
          setStatus("error");
          setErrorMessage(response.data.message || "Failed to unsubscribe.");
        }
      } catch (error) {
        console.error("Unsubscribe error:", error);
        setStatus("error");
        setErrorMessage(
          error.response?.data?.detail || "Failed to process your request. Please try again."
        );
      }
    };

    processUnsubscribe();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_eff51d80-5789-419f-a2d8-ecfb7c53e41f/artifacts/rtlggz3k_Invigor%20Digital%20logo.png" 
              alt="Invigor Digital" 
              className="h-10 w-auto"
            />
          </div>
        </div>
      </header>

      <main className="flex items-center justify-center min-h-[calc(100vh-200px)] p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            {status === "processing" && (
              <>
                <Loader2 size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
                <h2 className="text-xl font-semibold">Processing...</h2>
                <p className="text-muted-foreground mt-2">Just a moment</p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h2 className="text-xl font-semibold">You have been unsubscribed</h2>
                <p className="text-muted-foreground mt-2 mb-6">
                  You will no longer receive review request emails from us.
                </p>
                <p className="text-sm text-muted-foreground">
                  If you unsubscribed by mistake, please contact us at{" "}
                  <a href="mailto:info@invigordigital.com" className="text-blue-600 hover:underline">
                    info@invigordigital.com
                  </a>
                </p>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle size={32} className="text-red-600" />
                </div>
                <h2 className="text-xl font-semibold text-red-700">Something went wrong</h2>
                <p className="text-muted-foreground mt-2 mb-6">{errorMessage}</p>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/")}
                >
                  Go to Homepage
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white/50">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} Invigor Digital. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
